import React from 'react';
import styled, { useTheme } from 'styled-components';
import Modal from '../../Modal/Modal';
import { H2, LabelUppercase, PSmall } from '../../elements/Text';
import { CloseIcon } from '../../Icons';
import { useGetCompany } from '../../../hooks/use-hydra/admin';
import { FormInput, FormLabel } from '../../Form';
import { useParams } from 'react-router-dom';
import Button from '../../elements/Button';
import useAuth from '../../../hooks/use-auth';
import { toast } from 'react-toastify';
import { addCohort } from '../../../api/admin';
import { useMutation } from 'react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

const StyledFormLabel = styled(FormLabel)`
  color: ${({ theme }) => theme.colors.textActive};
  font-weight: 600;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-top: 32px;
  width: 100%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  padding-bottom: 72px;
`;
const Wrapper = styled(Modal)`
  padding-bottom: 72px;
  padding-top: 64px;
  width: 80vw;
  min-width: 400px;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const CloseButton = styled(CloseIcon)`
  padding: 0;
`;

const Header = styled(H2)`
  margin: 0;
  padding: 0;
`;

const DatePicker = styled(FormInput).attrs({
  type: 'date',
})`
  margin-top: 8px;
`;

const DateTimeInput = styled(FormInput)`
  width: 100%;
  padding-bottom: 0;
`;

const DateTimeContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  flex: 1;
`;

const AddButton = styled(Button)`
  border: none;
`;

const RemoveButton = styled(Button)`
  border: none;
  background: none;
  margin: 0;
  gap: 0;
  margin-left: 8px;
`;

const DateTimeInputGroup = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const TwoColumnRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
`;

const roundToNearest30Minutes = (date: Date): Date => {
  const newDate = new Date(date);
  const minutes = newDate.getMinutes();
  const remainder = minutes % 30;

  if (remainder > 0) {
    // Round up to next 30 minute block
    newDate.setMinutes(minutes + (30 - remainder));
    // Reset seconds and milliseconds
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
  }

  return newDate;
};

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

function formatKickoffTime(date: Date, userTz: string) {
  const offset = dayjs(date).utcOffset();

  const dUtc = dayjs(date).utcOffset(offset);

  const dLocal = dayjs.utc(date).tz(userTz).add(offset, 'minutes');
  return `${dUtc.format('dddd MMMM Do h:mm A UTC')} | ${dLocal.format(
    'dddd MMMM Do h:mm A z'
  )}`;
}

function formatOfficeHourTime(date: Date, userTz: string) {
  const offset = dayjs(date).utcOffset();

  const dUtc = dayjs(date).utcOffset(offset);

  const dLocal = dayjs.utc(date).tz(userTz).add(offset, 'minutes');
  return `${dUtc.format('dddd h:mm A UTC')} | ${dLocal.format(
    'dddd h:mm A z'
  )}`;
}

const FormattedTimeLabel = styled(LabelUppercase)`
  margin-top: 4px;
  color: ${({ theme }) => theme.colors.textRegular};
`;

export default function AddCohortModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { getTokenSilently } = useAuth();

  const { id } = useParams<{ id: string }>();

  const {
    data: company,
    refetch: refetchCompany,
    isLoading: isLoadingCompany,
  } = useGetCompany(parseInt(id));
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');
  const [kickOffTimes, setKickOffTimes] = React.useState<Date[]>([]);
  const [officeHourTimes, setOfficeHourTimes] = React.useState<Date[]>([]);

  const addCohortMutation = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();
      await addCohort(token, {
        companyId: id,
        startDate,
        endDate,
        kickOffTimes: kickOffTimes.map((time) => {
          const d = dayjs(time);
          return dayjs.utc(d.format('YYYY-MM-DDTHH:mm')).toISOString();
        }),
        officeHourTimes: officeHourTimes.map((time) => {
          const d = dayjs(time);
          return dayjs.utc(d.format('YYYY-MM-DDTHH:mm')).toISOString();
        }),
      });
    },
    onSuccess: async () => {
      await refetchCompany();
      toast.success('Cohort added successfully');
      onClose();
    },
    onError: () => {
      toast.error('Something went wrong when adding the cohort');
    },
  });

  const minStartDate = dayjs
    .utc()
    .subtract(1, 'month')
    .startOf('week')
    .add(1, 'day') // monday, bc week starts on Sunday
    .format('YYYY-MM-DD');

  const minEndDate = dayjs
    .utc(startDate)
    .add(4, 'week')
    .subtract(1, 'day')
    .startOf('day') // move to previous sunday
    .format('YYYY-MM-DD');

  const areDatesValid =
    startDate &&
    endDate &&
    dayjs.utc(startDate).day() === 1 && // Monday is 1
    dayjs.utc(endDate).day() === 0 && // Sunday is 0
    dayjs.utc(endDate).add(1, 'day').diff(dayjs.utc(startDate), 'week') === 4 &&
    (kickOffTimes.length > 0 || !company?.settings.KICK_OFF) &&
    officeHourTimes.length > 0;

  const handleAddKickOffTime = () => {
    setKickOffTimes([...kickOffTimes, roundToNearest30Minutes(new Date())]);
  };

  const handleAddOfficeHourTime = () => {
    const latestOfficeHour =
      officeHourTimes.length > 0
        ? officeHourTimes.sort((a, b) => a.getTime() - b.getTime())[
            officeHourTimes.length - 1
          ]
        : null;
    const newOfficeHour = latestOfficeHour
      ? dayjs.utc(latestOfficeHour).add(30, 'minutes').toDate()
      : dayjs.utc(startDate).toDate();

    setOfficeHourTimes([
      ...officeHourTimes,
      roundToNearest30Minutes(newOfficeHour),
    ]);
  };

  const handleUpdateKickOffTime = (index: number, value: string) => {
    const newTimes = [...kickOffTimes];
    newTimes[index] = roundToNearest30Minutes(new Date(value));
    setKickOffTimes(newTimes);
  };

  const handleUpdateOfficeHourTime = (index: number, value: string) => {
    const newTimes = [...officeHourTimes];
    newTimes[index] = roundToNearest30Minutes(new Date(value));
    setOfficeHourTimes(newTimes);
  };

  const handleRemoveKickOffTime = (index: number) => {
    const newTimes = [...kickOffTimes];
    newTimes.splice(index, 1);
    setKickOffTimes(newTimes);
  };

  const handleRemoveOfficeHourTime = (index: number) => {
    const newTimes = [...officeHourTimes];
    newTimes.splice(index, 1);
    setOfficeHourTimes(newTimes);
  };
  const theme = useTheme();
  const userTimezone = dayjs.tz.guess();

  const areAllOfficeHoursInTheFirstWeekOfTheCohort = officeHourTimes.every(
    (time) => {
      const officeHour = dayjs.utc(time);
      const cohortStart = dayjs.utc(startDate);
      const cohortEndOfFirstWeek = dayjs.utc(startDate).endOf('week');
      return (
        officeHour.isSameOrAfter(cohortStart) &&
        officeHour.isBefore(cohortEndOfFirstWeek)
      );
    }
  );

  if (!isOpen) {
    return null;
  }

  return (
    <Wrapper>
      <HeaderRow>
        <Header>Add New Cohort to {company?.name}</Header>
        <CloseButton onClick={onClose} />
      </HeaderRow>
      <Content>
        <PSmall>
          For now, cohorts must have 4 weeks, starting on Monday and ending on
          Sunday.
        </PSmall>
        <TwoColumnRow>
          <ColumnContainer>
            <StyledFormLabel>Start Date</StyledFormLabel>
            <DatePicker
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
              }}
              min={minStartDate}
              step="7"
            />
          </ColumnContainer>
          <ColumnContainer>
            <StyledFormLabel>End Date</StyledFormLabel>
            <DatePicker
              isDisabled={!startDate}
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
              }}
              min={minEndDate}
              max={minEndDate}
              step="7"
            />
          </ColumnContainer>
        </TwoColumnRow>
        <PSmall>
          For confirmed office hours, we'll create a weekly recurring event for
          the duration of the cohort. You only need to set the first one.
        </PSmall>
        <PSmall>
          PS: we're rounding to the nearest 30 minute block for kick-off and
          office hour times.
        </PSmall>
        <TwoColumnRow>
          <DateTimeContainer>
            <StyledFormLabel>Kick-Off Times (UTC)</StyledFormLabel>
            {company?.settings.KICK_OFF ? (
              <>
                {kickOffTimes.map((time, index) => (
                  <React.Fragment key={`kickoff-${index}`}>
                    <DateTimeInputGroup>
                      <DateTimeInput
                        type="datetime-local"
                        value={dayjs(time).format('YYYY-MM-DDTHH:mm')}
                        onChange={(e) =>
                          handleUpdateKickOffTime(index, e.target.value)
                        }
                      />
                      <RemoveButton
                        label=""
                        faIcon="fa-solid fa-times"
                        onClick={() => handleRemoveKickOffTime(index)}
                        color={theme.colors.textRegular}
                      />
                    </DateTimeInputGroup>
                    <FormattedTimeLabel>
                      {formatKickoffTime(time, userTimezone)}
                    </FormattedTimeLabel>
                  </React.Fragment>
                ))}
                <AddButton
                  onClick={handleAddKickOffTime}
                  label="Add Kick-Off Time"
                  showIconBeforeLabel
                  faIcon="fa-solid fa-plus"
                  color={theme.colors.textRegular}
                />
              </>
            ) : (
              <PSmall>Kick-off times are disabled for this company.</PSmall>
            )}
          </DateTimeContainer>

          <DateTimeContainer>
            <StyledFormLabel>First Office Hour Times (UTC)</StyledFormLabel>

            {officeHourTimes.map((time, index) => (
              <React.Fragment key={`officehour-${index}`}>
                <DateTimeInputGroup>
                  <DateTimeInput
                    min={dayjs.utc(startDate).format('YYYY-MM-DDTHH:mm')}
                    max={dayjs
                      .utc(startDate)
                      .endOf('week')
                      .format('YYYY-MM-DDTHH:mm')}
                    type="datetime-local"
                    value={dayjs(time).format('YYYY-MM-DDTHH:mm')}
                    onChange={(e) =>
                      handleUpdateOfficeHourTime(index, e.target.value)
                    }
                  />
                  <RemoveButton
                    label=""
                    faIcon="fa-solid fa-times"
                    onClick={() => handleRemoveOfficeHourTime(index)}
                    color={theme.colors.textRegular}
                  />
                </DateTimeInputGroup>
                <FormattedTimeLabel>
                  {formatOfficeHourTime(time, userTimezone)}
                </FormattedTimeLabel>
              </React.Fragment>
            ))}
            <AddButton
              onClick={handleAddOfficeHourTime}
              label="Add Office Hour Time"
              showIconBeforeLabel
              faIcon="fa-solid fa-plus"
              color={theme.colors.textRegular}
            />
          </DateTimeContainer>
        </TwoColumnRow>

        <Row>
          <Button
            label="Add Cohort"
            type="primary"
            showIconBeforeLabel
            faIcon="fa-solid fa-save"
            isDisabled={
              !areDatesValid ||
              !areAllOfficeHoursInTheFirstWeekOfTheCohort ||
              addCohortMutation.isLoading
            }
            onClick={() => {
              addCohortMutation.mutate();
            }}
            isLoading={addCohortMutation.isLoading || isLoadingCompany}
          />
        </Row>
      </Content>
    </Wrapper>
  );
}
